import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { Hint, Stat, Stats } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { useDataTable } from 'shared/hooks/use-data-table'
import { cn } from 'shared/lib/utils'
import { formatDayTime } from 'shared/lib/utils/date'

/**
 * The Shift differences (ADR-0035): every closed Shift whose counted drawer
 * differed from the Cashbox, with the operator the difference is recorded against
 * and their comment. The cashier and support both see it — on the central cashbox
 * and in the support section — so it lives here, shared by both.
 *
 * A difference is TezGo's, never the partner's (ADR-0036): a shortage is the
 * operator's debt, a surplus is TezGo's. In the hub's look: the totals as figures,
 * the Shifts as one table, the amounts right in «сум», why it is the operator's
 * behind the «i» of the difference's column.
 */

type Difference = RouterOutputs['shifts']['Differences'][number]

function sum(amount: number): string {
	return amount.toLocaleString('ru-RU')
}

export function ShiftDifferences() {
	const query = useQuery(trpc.shifts.Differences.queryOptions({}))
	return <ShiftDifferenceList items={query.data} loading={query.isFetching && !query.data} />
}

export function ShiftDifferenceList({
	items,
	loading = false,
}: {
	items?: Difference[]
	loading?: boolean
}) {
	const { t } = useTranslate()
	const data = useMemo(() => items ?? [], [items])
	const shortage = data.reduce((total, d) => total + Math.max(0, -(d.difference ?? 0)), 0)
	const surplus = data.reduce((total, d) => total + Math.max(0, d.difference ?? 0), 0)

	const columns = useMemo<ColumnDef<Difference>[]>(
		() => [
			{ id: 'point', header: t('shiftDifferences.colPoint'), cell: ({ row: { original: d } }) => d.pointCode },
			{
				id: 'operator',
				header: t('shift.operator'),
				cell: ({ row: { original: d } }) => (
					<div className='flex flex-wrap items-center gap-2'>
						<span>{d.operatorName}</span>
						{d.closedOnBehalf && (
							<Badge variant='secondary' data-testid={`shift-difference-on-behalf-${d.shiftId}`}>
								{t('shift.closedOnBehalf')}
							</Badge>
						)}
					</div>
				),
			},
			{
				id: 'closed',
				header: t('shiftDifferences.colClosed'),
				cell: ({ row: { original: d } }) => (
					<span className='text-muted-foreground tabular-nums'>{d.closedAt ? formatDayTime(d.closedAt) : '—'}</span>
				),
			},
			{
				id: 'counted',
				header: t('shiftDifferences.colCounted'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row: { original: d } }) => sum(d.countedCash ?? 0),
			},
			{
				id: 'expected',
				header: t('shiftDifferences.colExpected'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row: { original: d } }) => sum(d.expectedCash ?? 0),
			},
			{
				id: 'difference',
				header: () => (
					<span className='inline-flex items-center gap-1'>
						{t('shiftDifferences.colDifference')}
						<Hint>{t('shift.differencesExplain')}</Hint>
					</span>
				),
				meta: { headClassName: 'text-right', cellClassName: 'text-right' },
				cell: ({ row: { original: d } }) => {
					const short = (d.difference ?? 0) < 0
					return (
						<div className='flex items-center justify-end gap-2'>
							<Badge variant={short ? 'destructive' : 'secondary'}>{short ? t('shift.shortage') : t('shift.surplus')}</Badge>
							<span className={cn('tabular-nums', short && 'text-destructive')}>{sum(Math.abs(d.difference ?? 0))}</span>
						</div>
					)
				},
			},
			{
				id: 'comment',
				header: t('shiftDifferences.colComment'),
				meta: { cellClassName: 'whitespace-normal text-muted-foreground' },
				cell: ({ row: { original: d } }) => d.comment ?? '',
			},
		],
		[t],
	)

	const { table } = useDataTable({
		data,
		columns,
		rowCount: data.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		autoResetPageIndex: false,
		getRowId: (d) => d.shiftId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	if (loading) {
		return <div className='text-sm text-muted-foreground'>{t('shift.loading')}</div>
	}
	return (
		<div className='flex flex-col gap-4'>
			<Stats testId='shift-differences-stats'>
				<Stat label={t('shiftDifferences.statCount')} value={data.length} />
				<Stat label={t('shiftDifferences.statShortage')} value={sum(shortage)} tone='bad' />
				<Stat label={t('shiftDifferences.statSurplus')} value={sum(surplus)} />
			</Stats>
			<DataTable
				table={table}
				withPagination={false}
				emptyText={<span data-testid='shift-differences-empty'>{t('shift.differencesEmpty')}</span>}
				row={{ attributes: (d) => ({ 'data-testid': `shift-difference-${d.shiftId}` }) }}
			/>
		</div>
	)
}
